import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Portal } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { TWclassNames } from "../Div";

/**
 * Single-select entity picker — Promang component kit (PROMANG_COMPONENTS.md §3,
 * `pm-picker*` classes in src/index.css).
 *
 * Trigger field + search panel with optional segmented filters and keyboard
 * navigation (↑↓ ↵ esc). Desktop: panel anchored under the field. Phones: bottom
 * sheet. The panel renders in a Headless UI Portal so it is not clipped by
 * dialogs and stays inside a PMDialog's focus trap.
 *
 * Items are `{ id, primary, secondary?, leading?, tag?, object? }`.
 * `loadOptions({ query, filter })` returns (a promise of) items; stale responses
 * are dropped.
 */
const SEARCH_DEBOUNCE_MS = 200;
const PANEL_GAP = 6;
const PANEL_MIN_WIDTH = 320;
const PANEL_MIN_HEIGHT_BELOW = 260;
const VIEWPORT_MARGIN = 8;
const SHEET_BREAKPOINT = 640;
/** Above PMDialog (2000) and the legacy selector dialogs (3000). */
const PANEL_Z_INDEX = 3100;

export function computePanelPosition(anchor) {
  const rect = anchor.getBoundingClientRect();
  const width = Math.min(Math.max(rect.width, PANEL_MIN_WIDTH), window.innerWidth - VIEWPORT_MARGIN * 2);
  const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), window.innerWidth - width - VIEWPORT_MARGIN);
  const spaceBelow = window.innerHeight - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN;
  const spaceAbove = rect.top - PANEL_GAP - VIEWPORT_MARGIN;
  if (spaceBelow >= PANEL_MIN_HEIGHT_BELOW || spaceBelow >= spaceAbove) {
    return { top: rect.bottom + PANEL_GAP, left, width, maxHeight: spaceBelow };
  }
  return { bottom: window.innerHeight - rect.top + PANEL_GAP, left, width, maxHeight: spaceAbove };
}

function useIsSheet() {
  const query = () => typeof window !== "undefined" && window.innerWidth < SHEET_BREAKPOINT;
  const [isSheet, setIsSheet] = useState(query);
  useEffect(() => {
    const onResize = () => setIsSheet(query());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isSheet;
}

/**
 * Optional label quick-link (legacy `labelAction` from moduleSelectors / tags):
 * `{ action }` opens a dialog; `{ path }` navigates (new tab). Callers usually
 * pass this only when a value is selected.
 */
export function PMEntityPicker({
  label,
  labelAction = null,
  required = false,
  value = null,
  placeholder,
  searchPlaceholder,
  disabled = false,
  error,
  hint,
  loadOptions,
  filters = [],
  defaultFilter,
  onSelect,
  allowClear = true,
  emptyText,
  pageSize,
  footerAction,
  children,
}) {
  const { t } = useTranslation();
  const baseId = useId();
  const triggerId = `${baseId}-trigger`;
  const listId = `${baseId}-list`;
  const messageId = `${baseId}-message`;
  const isSheet = useIsSheet();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(defaultFilter);
  const [result, setResult] = useState({ status: "idle", items: [] });
  const [activeIndex, setActiveIndex] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [position, setPosition] = useState(null);

  const fieldRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const requestRef = useRef(0);

  // A new context (e.g. another customer) resets the filter to its default.
  useEffect(() => setFilter(defaultFilter), [defaultFilter]);

  const close = useCallback((returnFocus) => {
    requestRef.current += 1;
    setOpen(false);
    setQuery("");
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const openPanel = () => {
    if (disabled || open) return;
    setActiveIndex(0);
    setResult({ status: "loading", items: [] });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const request = ++requestRef.current;
    const context = { query, filter, reloadKey, loadOptions };
    setResult({ ...context, status: "loading", items: [] });
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => loadOptions({ query: query.trim(), filter }))
        .then((items) => {
          if (request !== requestRef.current) return;
          setResult({ ...context, status: "ready", items: Array.isArray(items) ? items : [] });
          setActiveIndex(0);
        })
        .catch(() => {
          if (request !== requestRef.current) return;
          setResult({ ...context, status: "error", items: [] });
        });
    }, query ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      clearTimeout(timer);
      requestRef.current += 1;
    };
  }, [open, query, filter, reloadKey, loadOptions]);

  useLayoutEffect(() => {
    if (!open || isSheet) return undefined;
    const place = () => fieldRef.current && setPosition(computePanelPosition(fieldRef.current));
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, isSheet]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onOutsideInteraction = (event) => {
      if (fieldRef.current?.contains(event.target) || panelRef.current?.contains(event.target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onOutsideInteraction, true);
    document.addEventListener("focusin", onOutsideInteraction, true);
    return () => {
      document.removeEventListener("mousedown", onOutsideInteraction, true);
      document.removeEventListener("focusin", onOutsideInteraction, true);
    };
  }, [open, close]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  // Hide old results immediately, including the render before the load effect runs.
  const currentResult = result.loadOptions === loadOptions && result.query === query
    && result.filter === filter && result.reloadKey === reloadKey;
  const status = currentResult ? result.status : "loading";
  const items = status === "ready" ? result.items : [];
  const select = (item) => {
    if (disabled || status !== "ready") return;
    onSelect?.(item);
    close(true);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(items.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (items[activeIndex]) select(items[activeIndex]);
    }
  };

  const onPickerKeyDown = (event) => {
    if (open && event.key === "Escape") {
      // preventDefault stops the surrounding PMDialog from closing too.
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
  };

  const clearable = allowClear && !disabled && value?.id != null;
  const describedBy = error || hint ? messageId : undefined;
  const panelStyle = isSheet
    ? { left: 0, right: 0, bottom: 0, maxHeight: "85dvh", zIndex: PANEL_Z_INDEX + 1 }
    : { ...position, zIndex: PANEL_Z_INDEX, visibility: position ? "visible" : "hidden" };
  return (
    <div className="min-w-0" onKeyDown={onPickerKeyDown}>
      {label && (
        <div className="pm-label mb-2">
          {labelAction?.action ? (
            <button
              type="button"
              className="pm-label-link"
              onClick={labelAction.action}
              disabled={disabled}
            >
              {label}
            </button>
          ) : labelAction?.path ? (
            <Link
              to={labelAction.path}
              target="_blank"
              rel="noopener noreferrer"
              className="pm-label-link"
            >
              {label}
            </Link>
          ) : (
            <label htmlFor={triggerId}>{label}</label>
          )}
          {required && <span style={{ color: "var(--pls-bad)" }}> *</span>}
        </div>
      )}
      <div
        ref={fieldRef}
        className="pm-picker-trigger"
        data-open={open || undefined}
        data-invalid={error ? true : undefined}
        data-disabled={disabled || undefined}
        onClick={(event) => {
          if (!event.target.closest?.("button")) openPanel();
        }}
      >
        <button
          id={triggerId}
          ref={triggerRef}
          type="button"
          className="pm-picker-trigger__main"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onClick={() => (open ? close(false) : openPanel())}
        >
          {value ? value.leading : null}
          <span className="pm-picker-trigger__text">
            {value ? (
              <>
                <span className="pm-picker-trigger__primary">{value.primary}</span>
                {value.secondary && <span className="pm-picker-secondary">{value.secondary}</span>}
              </>
            ) : (
              <span className="pm-picker-trigger__placeholder">{placeholder}</span>
            )}
          </span>
          {value?.tag}
        </button>
        {clearable && (
          <button type="button" className="pm-picker-clear" aria-label={t("pickers.clear")} onClick={() => onSelect?.(null)}>
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <ChevronDownIcon className="pm-picker-trigger__chevron" aria-hidden="true" />
      </div>
      {error ? (
        <p id={messageId} className="pm-field-error" role="alert">{error}</p>
      ) : hint ? (
        <p id={messageId} className="pm-help mt-2">{hint}</p>
      ) : null}
      {children}

      {open && (
        <Portal>
          {isSheet && (
            <div className="pm-picker-scrim fixed inset-0" style={{ zIndex: PANEL_Z_INDEX }} aria-hidden="true" onClick={() => close(true)} />
          )}
          <div ref={panelRef} className={TWclassNames("pm-picker-panel fixed", isSheet ? "pm-picker-panel--sheet" : "")} style={panelStyle}>
            <div className="pm-picker-head">
              <div className="pm-picker-search">
                <MagnifyingGlassIcon className="h-4 w-4 flex-none" style={{ color: "var(--pls-muted)" }} aria-hidden="true" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={searchPlaceholder || t("pickers.search")}
                  role="combobox"
                  aria-label={searchPlaceholder || t("pickers.search")}
                  aria-expanded="true"
                  aria-controls={listId}
                  aria-autocomplete="list"
                  aria-activedescendant={items[activeIndex] ? `${listId}-${activeIndex}` : undefined}
                  autoComplete="off"
                />
                {query && (
                  <button type="button" className="pm-picker-clear" aria-label={t("pickers.clearSearch")} onClick={() => { setQuery(""); searchRef.current?.focus(); }}>
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
              {filters.length > 1 && (
                <div className="pm-segmented pm-segmented--sm" role="radiogroup" aria-label={t("pickers.filters")}>
                  {filters.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      role="radio"
                      aria-checked={filter === option.key}
                      className={TWclassNames("pm-segmented__option", filter === option.key ? "is-active" : "")}
                      onClick={() => { setFilter(option.key); searchRef.current?.focus(); }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <ul id={listId} ref={listRef} role="listbox" aria-label={label} aria-busy={status === "loading"} className="pm-picker-list">
              {status === "loading" && [0, 1, 2, 3].map((row) => (
                <li key={`skeleton-${row}`} aria-hidden="true" className="pm-picker-option">
                  <span className="pls-skel" style={{ width: 30, height: 30, borderRadius: "50%" }} />
                  <span className="flex flex-1 flex-col gap-2">
                    <span className="pls-skel" style={{ height: 10, width: "60%" }} />
                    <span className="pls-skel" style={{ height: 8, width: "35%" }} />
                  </span>
                </li>
              ))}
              {items.map((item, index) => (
                <li
                  key={item.id}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={value?.id != null && String(value.id) === String(item.id)}
                  data-index={index}
                  className={TWclassNames("pm-picker-option", index === activeIndex ? "is-active" : "")}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(item)}
                >
                  {item.leading}
                  <span className="min-w-0 flex-1">
                    <span className="pm-picker-option__primary">{item.primary}</span>
                    {item.secondary && <span className="pm-picker-secondary">{item.secondary}</span>}
                  </span>
                  {item.tag}
                  <CheckIcon className="pm-picker-option__check" aria-hidden="true" />
                </li>
              ))}
            </ul>
            {status === "ready" && !items.length && (
              <p className="pm-picker-state" role="status">
                {query.trim() ? t("pickers.emptyQuery", { query: query.trim() }) : emptyText || t("pickers.empty")}
              </p>
            )}
            {status === "error" && (
              <div className="pm-picker-state" role="alert">
                {t("pickers.loadFailed")}{" "}
                <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => setReloadKey((key) => key + 1)}>
                  {t("pickers.retry")}
                </button>
              </div>
            )}
            {status === "ready" && pageSize && items.length >= pageSize && (
              <p className="pm-help px-4 pb-2">{t("pickers.moreResults", { count: pageSize })}</p>
            )}
            {(footerAction || !isSheet) && (
              <div className="pm-picker-footer">
                {!isSheet && (
                  <>
                    <span>{t("pickers.hintMove")}</span>
                    <span>{t("pickers.hintSelect")}</span>
                    <span>{t("pickers.hintClose")}</span>
                  </>
                )}
                {footerAction && (
                  <button
                    type="button"
                    className="pm-btn pm-btn--secondary pm-btn--sm pm-picker-footer__action"
                    onClick={() => { close(false); footerAction.onClick(); }}
                  >
                    <PlusIcon className="h-4 w-4" aria-hidden="true" />
                    {footerAction.label}
                  </button>
                )}
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}
